'use client'

/**
 * Segment analytics supports two modes depending on OneTrust configuration:
 *
 * 1. **No OneTrust data domain script env** (NEXT_PUBLIC_ONE_TRUST_DATA_DOMAIN_SCRIPT unset):
 *    We keep the current behavior: track everything and do not show a consent banner.
 *    Analytics loads immediately with the write key and plugins.
 *
 * 2. **OneTrust data domain script env set**:
 *    We still track everything, but we show the consent banner and integrate Segment
 *    with OneTrust via the consent wrapper. The analytics instance is created empty
 *    first, then loaded through withOneTrust() so consent preferences are respected
 *    and the global `analytics` is exposed for OneTrust's OptanonWrapper.
 */

import { withOneTrust } from '@segment/analytics-consent-wrapper-onetrust'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { usePathname } from 'next/navigation'
import { createContext, useContext, useEffect, useRef } from 'react'
import { defaultParametersPlugin } from '../plugins/default-parameters'
import { pageviewIdPlugin } from '../plugins/pageview-id'

const AnalyticsContext = createContext<AnalyticsBrowser | null>(null)

export type SegmentTrackMeta = {
  getPageviewId: () => string
  getPageName: () => string
  setPageName: (name: string) => void
}

const TrackMetaContext = createContext<SegmentTrackMeta | null>(null)

export const SegmentAnalyticsProvider = ({
  children,
  writeKey,
  oneTrustDataDomainScript,
}: {
  children: React.ReactNode
  writeKey: string
  oneTrustDataDomainScript?: string
}) => {
  const pathname = usePathname()
  const analyticsRef = useRef<AnalyticsBrowser | null>(null)
  const loadedRef = useRef<boolean>(false)
  const pageviewIdRef = useRef<string>(crypto.randomUUID())
  // Skip first-mount rotate: init UUID must survive so PageViewed child effects keep it.
  const prevPathnameRef = useRef(pathname)
  const pageNameRef = useRef<string>('')
  const trackMetaRef = useRef<SegmentTrackMeta>({
    getPageviewId: () => pageviewIdRef.current,
    getPageName: () => pageNameRef.current,
    setPageName: (name: string) => {
      pageNameRef.current = name
    },
  })

  // Soft-nav rotate/clear during render (before children). An effect would run after
  // PageViewed's render-phase setPageName and wipe page_name for later tracks.
  if (prevPathnameRef.current !== pathname) {
    prevPathnameRef.current = pathname
    pageviewIdRef.current = crypto.randomUUID()
    pageNameRef.current = ''
  }

  if (!analyticsRef.current) {
    analyticsRef.current = oneTrustDataDomainScript
      ? new AnalyticsBrowser()
      : AnalyticsBrowser.load({
          writeKey,
          plugins: [pageviewIdPlugin(() => pageviewIdRef.current), defaultParametersPlugin()],
        })
  }

  useEffect(() => {
    if (oneTrustDataDomainScript && analyticsRef.current && !loadedRef.current) {
      withOneTrust(analyticsRef.current).load({
        writeKey,
        plugins: [pageviewIdPlugin(() => pageviewIdRef.current), defaultParametersPlugin()],
      })

      // Expose Segment instance globally so OneTrust OptanonWrapper can call analytics.track() on `OptanonWrapper` function
      window.analytics = analyticsRef.current

      loadedRef.current = true
    }
  }, [oneTrustDataDomainScript, writeKey])

  return (
    <AnalyticsContext.Provider value={analyticsRef.current}>
      <TrackMetaContext.Provider value={trackMetaRef.current}>{children}</TrackMetaContext.Provider>
    </AnalyticsContext.Provider>
  )
}

export const useSegmentAnalytics = () => {
  const analytics = useContext(AnalyticsContext)

  if (!analytics) {
    throw new Error('useSegmentAnalytics must be used within a SegmentAnalyticsProvider')
  }

  return analytics
}

export const useSegmentTrackMeta = () => {
  const meta = useContext(TrackMetaContext)

  if (!meta) {
    throw new Error('useSegmentTrackMeta must be used within a SegmentAnalyticsProvider')
  }

  return meta
}
